import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Globe, ExternalLink, ArrowLeft, Rocket, AlertCircle, CheckCircle2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { internationalRegistrations, governmentRegistrations } from '../data/mockData';

const InternationalProcurement = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>International Procurement Registrations | SALAR Technologies</title>
        <meta name="description" content="SALAR Technologies active international procurement registrations across UN, EU, ADB, GeBIZ Singapore, Qatar Innolight, UAE Ministry of Finance, UNICC, UNDP and more — covering 5 continents." />
        <meta property="og:title" content="SALAR International Procurement Registrations" />
        <meta property="og:description" content="Active across UN, EU, ADB, GeBIZ Singapore, Qatar, UAE, and other multilateral and national procurement platforms — 5 continents." />
        <meta property="og:url" content="https://www.salartechnologies.com/international-procurement" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/international-procurement" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.88)), url('/img/world_map.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="intl-procurement-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="w-20 h-20 mx-auto mb-6 text-cyan-300" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">International Procurement Registrations</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Active registrations across multilateral, regional, and national procurement platforms — positioning SALAR
              Technologies for defense, government, and strategic-industry opportunities worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-12 bg-white/5 backdrop-blur-sm border-y border-blue-800/30">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto text-center">
            <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
              <div className="text-4xl font-bold text-white mb-2">10+</div>
              <div className="text-sm text-blue-200">Active International Platforms</div>
            </div>
            <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
              <div className="text-4xl font-bold text-white mb-2">UN · EU · ADB</div>
              <div className="text-sm text-blue-200">Multilateral Coverage</div>
            </div>
            <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
              <div className="text-4xl font-bold text-white mb-2">5 Continents</div>
              <div className="text-sm text-blue-200">Geographic Reach</div>
            </div>
          </div>
        </div>
      </section>

      {/* Registrations Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3 text-center">Registered Procurement Platforms</h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              Each entry below links directly to the official procurement platform where SALAR Technologies LLC is
              registered as a supplier, consultant, or vendor of record.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {internationalRegistrations.map((reg) => (
                <Card
                  key={reg.id}
                  data-testid={`intl-reg-${reg.id}`}
                  className="border-blue-500/30 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 transform hover:scale-[1.02] bg-blue-900/20"
                >
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-cyan-400/30 flex-shrink-0 bg-white p-2 flex items-center justify-center">
                        <img
                          src={reg.logo}
                          alt={`${reg.name} logo`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<div class="text-cyan-400 font-bold text-xl">${reg.fallbackText}</div>`;
                          }}
                        />
                      </div>
                      <CardTitle className="text-white text-lg flex items-start justify-between flex-1">
                        <span className="flex-1">{reg.name}</span>
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-blue-100 mb-3 font-semibold">{reg.status}</p>
                    <a
                      href={reg.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1"
                      data-testid={`intl-reg-link-${reg.id}`}
                    >
                      <ExternalLink size={14} />
                      View Platform
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* ADB Projects */}
            <div className="mt-12">
              <Card className="border-blue-500/30 bg-blue-900/20">
                <CardHeader>
                  <CardTitle className="text-white">Asian Development Bank Project References</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {governmentRegistrations.adbProjects.map((project, index) => (
                      <div key={index} className="p-4 bg-blue-950/40 rounded-lg border border-blue-500/30">
                        <p className="text-sm text-blue-100">
                          <strong>Section:</strong> {project.section} | <strong>TA:</strong> {project.ta} | <strong>Project:</strong> {project.project}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* European Deep-Tech Accelerator Pipeline */}
      <section className="py-16 bg-gradient-to-b from-blue-950/20 to-blue-900/30" data-testid="imec-istart-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <Card className="border-cyan-400/40 bg-gradient-to-br from-blue-900/40 to-cyan-900/20 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-blue-900/40 border-2 border-cyan-400/40 flex items-center justify-center flex-shrink-0">
                    <Rocket className="text-cyan-300" size={26} />
                  </div>
                  <div className="flex-1">
                    <Badge variant="outline" className="border-amber-400 text-amber-200 mb-3">
                      Application Status · Under Review
                    </Badge>
                    <CardTitle className="text-2xl md:text-3xl text-white leading-tight mb-2">
                      European Deep-Tech Accelerator Pipeline
                    </CardTitle>
                    <p className="text-cyan-200 text-base leading-relaxed">
                      imec.istart.nl 2026 Spring application submitted; German application tenant access enabled.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4 text-blue-100 leading-relaxed">
                  <p>
                    SALAR Technologies LLC has submitted an application to the imec.istart.nl 2026 Spring call as
                    part of its European deep-tech market-entry pathway. The application is currently under review
                    and does not represent acceptance, funding, investment, partnership, endorsement, or formal
                    program admission.
                  </p>
                  <p>
                    SALAR has also been enabled for the German imec.istart application tenant. Because imec.istart
                    limits applicants to a maximum of two applications across countries, SALAR is evaluating whether
                    a German submission is strategically appropriate before using its second application opportunity.
                  </p>
                  <p>
                    This pathway supports SALAR's international procurement and European market-entry strategy across
                    semiconductor reliability, electronics assurance, supply-chain intelligence, provenance/counterfeit-risk
                    control, and manufacturing-stability analytics for mission-critical hardware ecosystems.
                  </p>
                </div>

                {/* Status Bullets */}
                <div className="bg-blue-950/40 border border-blue-500/30 rounded-lg p-5" data-testid="imec-istart-status">
                  <h3 className="text-sm uppercase tracking-widest text-cyan-300 font-semibold mb-4">
                    Current Status
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-blue-100">
                      <CheckCircle2 className="text-cyan-400 mt-0.5 flex-shrink-0" size={18} />
                      <span>imec.istart.nl 2026 Spring application submitted</span>
                    </li>
                    <li className="flex items-start gap-3 text-blue-100">
                      <CheckCircle2 className="text-cyan-400 mt-0.5 flex-shrink-0" size={18} />
                      <span>Dutch preselection outcome expected by mid-May 2026</span>
                    </li>
                    <li className="flex items-start gap-3 text-blue-100">
                      <CheckCircle2 className="text-cyan-400 mt-0.5 flex-shrink-0" size={18} />
                      <span>German imec.istart application tenant access enabled</span>
                    </li>
                    <li className="flex items-start gap-3 text-blue-100">
                      <AlertCircle className="text-amber-400 mt-0.5 flex-shrink-0" size={18} />
                      <span>No acceptance, funding, investment, partnership, endorsement, or program admission claimed at this stage</span>
                    </li>
                  </ul>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <a href="https://www.imecistart.com/en/" target="_blank" rel="noopener noreferrer" data-testid="imec-istart-btn-1">
                    <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                      <ExternalLink className="mr-2" size={16} />
                      Visit imec.istart
                    </Button>
                  </a>
                  <a href="https://www.imecistart.com/en/apply" target="_blank" rel="noopener noreferrer" data-testid="imec-istart-btn-2">
                    <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400 hover:text-[#0a1628]">
                      <ExternalLink className="mr-2" size={16} />
                      imec.istart Apply Page
                    </Button>
                  </a>
                  <a href="https://www.imecistart.com/en/germany" target="_blank" rel="noopener noreferrer" data-testid="imec-istart-btn-3">
                    <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400 hover:text-[#0a1628]">
                      <ExternalLink className="mr-2" size={16} />
                      imec.istart Germany
                    </Button>
                  </a>
                  <a href="https://www.imec-int.com/en/about-us" target="_blank" rel="noopener noreferrer" data-testid="imec-istart-btn-4">
                    <Button variant="outline" className="border-blue-400 text-blue-200 hover:bg-blue-400 hover:text-[#0a1628]">
                      <ExternalLink className="mr-2" size={16} />
                      About imec
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* UAE — AI Video Analytics & Edge-IoT Visual Inspection (Public-Safe) */}
      <section className="py-16 bg-blue-950/20" data-testid="uae-rfi-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <Card className="border-emerald-400/40 bg-gradient-to-br from-blue-900/40 to-emerald-900/20 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-blue-900/40 border-2 border-emerald-400/40 flex items-center justify-center flex-shrink-0">
                    <Eye className="text-emerald-300" size={26} />
                  </div>
                  <div className="flex-1">
                    <Badge variant="outline" className="border-emerald-400 text-emerald-200 mb-3">
                      Market Opportunity Under Review
                    </Badge>
                    <CardTitle className="text-2xl md:text-3xl text-white leading-tight mb-3">
                      United Arab Emirates — AI Video Analytics &amp; Edge-IoT Visual Inspection
                    </CardTitle>
                    {/* Tag row */}
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="uae-rfi-tags">
                      {['UAE', 'AI Video Analytics', 'Edge Computing', 'IoT', 'Visual Inspection', 'Public Sector'].map((tag) => (
                        <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 border border-emerald-500/40 text-emerald-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* English Version */}
                <div data-testid="uae-rfi-english">
                  <Badge variant="outline" className="border-blue-400 text-blue-200 mb-4 text-xs uppercase tracking-widest">
                    English
                  </Badge>
                  <div className="space-y-4 text-blue-100 leading-relaxed">
                    <p>
                      SALAR Technologies LLC is tracking UAE public-sector interest in <strong className="text-white">AI video
                      analytics, edge computing, and IoT-based visual inspection systems</strong> for food safety, agriculture,
                      industrial inspection, and smart-infrastructure environments.
                    </p>
                    <p>
                      This area aligns with SALAR's focus on explainable AI decision support, real-time inspection
                      workflows, sensor-context fusion, traceable evidence capture, reliability analytics, and
                      manufacturing-stability logic.
                    </p>
                    <p>
                      SALAR's potential contribution is a pilot-ready <strong className="text-white">edge-AI visual inspection
                      decision layer</strong> that can support anomaly detection, defect classification, inspection
                      evidence records, IoT telemetry integration, and operator-ready corrective-action outputs.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="p-4 bg-blue-950/40 border border-blue-500/30 rounded-lg">
                      <h4 className="text-cyan-300 font-semibold text-sm uppercase tracking-wider mb-2">
                        Potential SALAR Role
                      </h4>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        AI / edge analytics specialist, pilot architecture provider, technical subcontractor, or
                        partner to a UAE-based systems integrator.
                      </p>
                    </div>
                    <div className="p-4 bg-blue-950/40 border border-blue-500/30 rounded-lg">
                      <h4 className="text-cyan-300 font-semibold text-sm uppercase tracking-wider mb-2">
                        Focus Areas
                      </h4>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        AI video analytics · Edge inference · IoT telemetry · Visual inspection · Traceable
                        inspection evidence · Quality and safety analytics
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-emerald-950/30 border-l-4 border-emerald-400 rounded">
                    <p className="text-blue-100 text-sm leading-relaxed mb-2">
                      <span className="text-emerald-300 font-semibold">Status: </span>
                      Market opportunity under review. Public procurement access may require supplier registration
                      through the relevant UAE government procurement portal.
                    </p>
                    <p className="text-blue-100 text-sm leading-relaxed">
                      <span className="text-emerald-300 font-semibold">Public Note: </span>
                      Full technical capability materials are available upon request for qualified government,
                      procurement, or systems-integration reviewers.
                    </p>
                  </div>
                </div>

                {/* Arabic Version */}
                <div className="pt-6 border-t border-blue-500/30" data-testid="uae-rfi-arabic" dir="rtl" lang="ar">
                  <Badge variant="outline" className="border-blue-400 text-blue-200 mb-4 text-xs uppercase tracking-widest">
                    العربية
                  </Badge>
                  <h3 className="text-xl text-white font-bold mb-4 leading-snug">
                    الإمارات العربية المتحدة — تحليل الفيديو بالذكاء الاصطناعي والفحص البصري عبر إنترنت الأشياء
                  </h3>
                  <div className="space-y-4 text-blue-100 leading-loose">
                    <p>
                      تتابع شركة <strong className="text-white">SALAR Technologies LLC</strong> اهتمام القطاع العام في
                      دولة الإمارات بمجالات <strong className="text-white">تحليل الفيديو بالذكاء الاصطناعي، والحوسبة
                      الطرفية، وأنظمة الفحص البصري القائمة على إنترنت الأشياء</strong>، وذلك في بيئات سلامة الغذاء،
                      والزراعة، والفحص الصناعي، والبنية التحتية الذكية.
                    </p>
                    <p>
                      يتوافق هذا المجال مع تركيز SALAR على دعم القرار القابل للتفسير بالذكاء الاصطناعي، وسير عمل
                      الفحص الفوري، ودمج بيانات المستشعرات، وتوثيق أدلة الفحص، وتحليلات الاعتمادية، ومنطق استقرار
                      التصنيع.
                    </p>
                    <p>
                      يمكن أن تتمثل مساهمة SALAR في طبقة قرار جاهزة للتجربة تعتمد على الذكاء الاصطناعي الطرفي لدعم
                      اكتشاف الشذوذ، وتصنيف العيوب، وتوثيق سجلات الفحص، ودمج بيانات إنترنت الأشياء، وتوليد مخرجات
                      تصحيحية قابلة للتنفيذ.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="p-4 bg-blue-950/40 border border-blue-500/30 rounded-lg">
                      <h4 className="text-cyan-300 font-semibold text-sm mb-2">
                        الدور المحتمل لـ SALAR
                      </h4>
                      <p className="text-blue-100 text-sm leading-loose">
                        مزود متخصص في تحليلات الذكاء الاصطناعي الطرفي، أو مزود بنية تجريبية، أو مقاول فرعي تقني،
                        أو شريك مع شركة تكامل أنظمة داخل دولة الإمارات.
                      </p>
                    </div>
                    <div className="p-4 bg-blue-950/40 border border-blue-500/30 rounded-lg">
                      <h4 className="text-cyan-300 font-semibold text-sm mb-2">
                        مجالات التركيز
                      </h4>
                      <p className="text-blue-100 text-sm leading-loose">
                        تحليل الفيديو بالذكاء الاصطناعي · الاستدلال الطرفي · بيانات إنترنت الأشياء · الفحص البصري ·
                        أدلة الفحص القابلة للتتبع · تحليلات الجودة والسلامة
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-emerald-950/30 border-r-4 border-emerald-400 rounded">
                    <p className="text-blue-100 text-sm leading-loose mb-2">
                      <span className="text-emerald-300 font-semibold">الحالة: </span>
                      فرصة سوقية قيد المراجعة. قد يتطلب الوصول إلى فرص المشتريات العامة التسجيل كمورد عبر بوابة
                      المشتريات الحكومية المختصة في دولة الإمارات.
                    </p>
                    <p className="text-blue-100 text-sm leading-loose">
                      <span className="text-emerald-300 font-semibold">ملاحظة عامة: </span>
                      تتوفر مواد القدرات الفنية الكاملة عند الطلب للجهات الحكومية أو جهات المشتريات أو شركاء تكامل
                      الأنظمة المؤهلين.
                    </p>
                  </div>
                </div>

                {/* Single Contact button */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <a
                    href="mailto:vikrant@salartechnologies.com?subject=UAE%20%E2%80%94%20AI%20Video%20Analytics%20%26%20Edge-IoT%20Visual%20Inspection%20Inquiry"
                    data-testid="uae-rfi-contact-btn"
                  >
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      Contact SALAR
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Back / CTA */}
      <section
        className="py-16 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/ai_fabric_hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="intl-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Looking for U.S. Government & Defense alignment?</h2>
            <p className="text-slate-300 mb-6">
              SALAR's U.S. federal registrations (UEI, CAGE, DUNS, NAICS, SAM) and DoD / DLA / DARPA / DIU / USSOCOM
              alignment are detailed on the Government &amp; Defense page.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/government-defense">
                <Button size="lg" className="bg-white text-[#0a1628] hover:bg-slate-100 font-semibold" data-testid="back-to-gov-defense">
                  <ArrowLeft className="mr-2" size={20} />
                  Government &amp; Defense Page
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0a1628]" data-testid="intl-contact-btn">
                  Contact SALAR
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternationalProcurement;
